import { useEffect, useReducer, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getRun, messageFor, OpsError, startRefresh,
  type Progress, type RefreshSource, type RunRow,
} from "./api";
import { followVerdict, IDLE, POLL_MS, refreshReducer } from "./follow";

export function useRefresh(areaName: string, attachTo: RunRow | null, xeroConfigured: boolean) {
  const client = useQueryClient();
  const [model, dispatch] = useReducer(refreshReducer, IDLE);
  const [xeroRefused, setXeroRefused] = useState(false);
  // Counted inside the fetch itself, so each poll counts exactly once
  // (the fulfilment_costs MonitorTab idiom); reset whenever a new run is followed.
  const polls = useRef({ empty: 0, failed: 0 });

  // C2 v1.4: the flag rides the Stock tab answer, so no second request per paint (D12 closed).
  const xeroAvailable = xeroConfigured && !xeroRefused;

  useEffect(() => {
    if (attachTo) dispatch({ type: "attach", run: attachTo });
  }, [attachTo]);

  useEffect(() => {
    polls.current = { empty: 0, failed: 0 };
  }, [model.runId]);

  const poll = useQuery({
    queryKey: ["ops", "runs", model.runId],
    queryFn: async () => {
      try {
        const res = await getRun(model.runId as string);
        const empty = Object.keys(res.data.progress).length === 0;
        polls.current = { empty: empty ? polls.current.empty + 1 : 0, failed: 0 };
        return res;
      } catch (error) {
        polls.current = { ...polls.current, failed: polls.current.failed + 1 };
        throw error;
      }
    },
    enabled: model.state === "running" && model.runId != null,
    refetchInterval: model.state === "running" ? POLL_MS : false,
    retry: false,
  });

  useEffect(() => {
    if (model.state !== "running" || model.runId == null) return;
    const answer = poll.data?.data;
    const run = answer && answer.run.run_id === model.runId ? answer.run : null;
    const verdict = followVerdict({
      run,
      emptyPolls: polls.current.empty,
      failedPolls: polls.current.failed,
      now: Date.now(),
    });
    dispatch({ type: "verdict", verdict, run });
    // Exactly one refetch per finished run: the reducer leaves `running` on this verdict.
    if (verdict === "done") void client.invalidateQueries({ queryKey: ["ops"] });
  }, [poll.data, poll.dataUpdatedAt, poll.errorUpdatedAt, model.state, model.runId, client]);

  async function start(source?: RefreshSource) {
    dispatch({ type: "press", source });
    try {
      const res = await startRefresh(source);
      dispatch({ type: "started", runId: res.data.run_id, started: res.data.started, source });
    } catch (error) {
      if (error instanceof OpsError && error.body?.error === "xero_refresh_not_configured") setXeroRefused(true);
      dispatch({ type: "refused", message: messageFor(error, areaName) });
    }
  }

  const running = model.state === "running";
  const progress: Progress = running ? poll.data?.data.progress ?? {} : {};
  const waiting = running && poll.isError ? "Still waiting for the run…" : null;

  return {
    state: model.state,
    progress,
    xeroAvailable,
    stalledRunId: model.state === "stalled" ? model.followed : null,
    message: waiting ?? model.message,
    start,
  };
}
